import { createStyles } from "@mantine/core";
import Image from "next/image";

import { Button } from "~/domains/design-system/Button";
import Dialog from "~/domains/design-system/components/Dialog/Dialog";
import { Text } from "~/domains/design-system/Text";
import { Title } from "~/domains/design-system/Title";

import {
  ContestManageModalBody,
  ContestManageModalDelimiter,
  ContestManageModalOrderedList,
  ContestManageModalOrderedListItem,
  ContestManageModalTitle,
} from "../commonStyles";

import recommendationImage from './recommendationImage.webp';
import useTranslation from "next-translate/useTranslation";

const useStyles = createStyles((theme) => ({
  subtitle: {
    textTransform: 'uppercase',
    textAlign: 'center',
    maxWidth: 320,
    margin: '16px auto 0',
    color: theme.colors.gray2[3],
  },
  button: {
    marginTop: theme.other.spacing._32,
  },
  recommendation: {
    display: 'flex',
    gap: theme.other.spacing._16,
    alignItems: 'center',
    marginTop: theme.other.spacing._32,
  },
  recommendationImage: {
    borderRadius: '50%',
    overflow: 'hidden',
    border: `1px solid ${theme.colors.gray2[1]}`,
  },
  recommendationName: {
    lineHeight: 1,
  },
  recommendationNote: {
    lineHeight: 1,
  },
}));

function SelfManageInfoModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const { classes, theme } = useStyles();
  const { t } = useTranslation('contest');

  return (
    <Dialog
      type={{
        desktop: 'modal',
        mobile: 'modal-full-screen',
      }}
      isOpen={isOpen}
      onClose={onClose}
    >
      <ContestManageModalBody>
        <ContestManageModalTitle>
          {t('manage.common.modals.selfManageInfoModal.title')}
        </ContestManageModalTitle>
        <Title variant="display-headline-expanded-medium" className={classes.subtitle}>
          {t('manage.common.modals.selfManageInfoModal.subtitle')}
        </Title>
        <ContestManageModalOrderedList>
          <ContestManageModalOrderedListItem>
            <div>
              <Title variant="title-large">
                {t('manage.common.modals.selfManageInfoModal.list.1.title')}
              </Title>
              <Text variant="body-large" color={theme.colors.gray2[3]}>
                {t('manage.common.modals.selfManageInfoModal.list.1.description')}
              </Text>
            </div>
          </ContestManageModalOrderedListItem>
          <ContestManageModalOrderedListItem>
            <div>
              <Title variant="title-large">
                {t('manage.common.modals.selfManageInfoModal.list.2.title')}
              </Title>
              <Text variant="body-large" color={theme.colors.gray2[3]}>
                {t('manage.common.modals.selfManageInfoModal.list.2.description')}
              </Text>
            </div>
          </ContestManageModalOrderedListItem>
        </ContestManageModalOrderedList>
        <ContestManageModalDelimiter />
        <Text variant="body-medium">
          {t('manage.common.modals.selfManageInfoModal.recommendation.text')}
        </Text>
        <div className={classes.recommendation}>
          <Image
            src={recommendationImage.src}
            alt={t('manage.common.modals.selfManageInfoModal.recommendation.author')}
            width={48}
            height={48}
            className={classes.recommendationImage}
          />
          <div>
            <Title
              variant="subhead-medium"
              className={classes.recommendationName}
            >
              {t('manage.common.modals.selfManageInfoModal.recommendation.author')}
            </Title>
            <Text
              variant="title-small"
              color={theme.colors.gray2[3]}
              className={classes.recommendationNote}
            >
              {t('manage.common.modals.selfManageInfoModal.recommendation.authorDescription')}
            </Text>
          </div>
        </div>
        <Button
          variant="primary-fill"
          fullWidth
          className={classes.button}
          onClick={onClose}
        >
          {t('manage.common.modals.selfManageInfoModal.cta')}
        </Button>
      </ContestManageModalBody>
    </Dialog>
  )
}

export default SelfManageInfoModal;
